import React from 'react';
import { lazyWithRetry } from '../../web/app-home/src/lib/lazyWithRetry';
import type { MiniAppManifest } from './types';

// Discover all mini-app manifests at build time (eager — small JSON files).
// This file lives at `mini-apps/_shared/registry.ts`, one directory above
// where these globs used to live (`mini-apps/_shared/components/MiniAppLayout.tsx`),
// so the relative pattern is `../*/...` instead of the original `../../*/...`.
// `import.meta.glob` patterns are resolved relative to the file they appear
// in, so they must stay physically inside `mini-apps/_shared/`.
const manifestModules = import.meta.glob('../*/manifest.json', { eager: true }) as Record<
  string,
  ({ default?: MiniAppManifest } & MiniAppManifest)
>;

// Discover all mini-app entry points (lazy — code-split per app)
const appModules = import.meta.glob('../*/index.tsx') as Record<
  string,
  () => Promise<{ default: React.ComponentType }>
>;

/**
 * The one Flow app. Playhouse and Action View were retired 2026-10 — see plan
 * 2026-10-13-001. Shared by the /flow shell and the AppDirectory's Flow section.
 */
export const FLOW_APP_SLUGS = ['command-center'] as const;

export interface MiniAppRegistryEntry {
  manifest: MiniAppManifest;
  Component: React.LazyExoticComponent<React.ComponentType>;
  /**
   * Warms the app's code-split chunk without rendering it (Vite caches the
   * dynamic import, so calling this repeatedly is free). The Flow shell preloads
   * inactive tabs during idle time so the first tab switch doesn't pay the
   * chunk download.
   */
  preload: () => Promise<unknown>;
}

/**
 * Discovers every mini-app under `mini-apps/*` (skipping `_`-prefixed
 * directories) and pairs each manifest with a lazy-loaded entry component.
 * Extracted from `MiniAppLayout.tsx` (U1, flow-unified-shell plan) so both
 * `MiniAppLayout` (`/tools`) and the Flow shell (`/flow`) can share one
 * discovery path instead of maintaining two globs that could drift.
 */
export function buildAppRegistry(): MiniAppRegistryEntry[] {
  const apps: MiniAppRegistryEntry[] = [];

  for (const [manifestPath, mod] of Object.entries(manifestModules)) {
    // Extract slug from path: ../launcher/manifest.json → launcher
    const match = manifestPath.match(/\.\.\/([^/]+)\/manifest\.json$/);
    if (!match) continue;
    const slug = match[1];

    // Skip internal directories
    if (slug.startsWith('_')) continue;

    const manifest = (mod as { default?: MiniAppManifest } & MiniAppManifest).default ?? mod;
    if (!manifest.slug || !manifest.name) continue;

    // Find matching entry point
    const entryPath = `../${slug}/index.tsx`;
    const loader = appModules[entryPath];
    if (!loader) continue;

    const Component = lazyWithRetry(loader);
    apps.push({ manifest, Component, preload: loader });
  }

  return apps.sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}
